import React, { useState, useEffect, useMemo } from 'react';
import { http } from '../../lib/http';
import MealPreferencesModal from '../shared/MealPreferencesModal';

const PreferencesTab = ({ setMessage, setError }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlanType, setFilterPlanType] = useState('all');

  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [showMenuModal, setShowMenuModal] = useState(false);
  const [menuDate, setMenuDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [menuData, setMenuData] = useState({
    breakfast: { name: '', protein_grams: '', calories: '' },
    lunch: { name: '', protein_grams: '', calories: '' },
    dinner: { name: '', protein_grams: '', calories: '' },
    chicken_meal: { name: '', protein_grams: '', calories: '' },
  });

  const VEG_TYPE_LABELS = {
    '5_days_veg': '5 Days Veg',
    '5_days_nonveg': '5 Days Non-Veg',
    '2NV_3V': 'Mixed (2NV + 3V)',
  };

  const MEAL_COUNT_LABELS = {
    '2_meals': '2 Meals/Day',
    '3_meals': '3 Meals/Day',
  };

  const filteredCustomers = useMemo(() => {
    let data = [...customers];

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      data = data.filter((customer) => {
        const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.toLowerCase();
        const email = (customer.email || '').toLowerCase();
        return fullName.includes(q) || email.includes(q);
      });
    }

    if (filterPlanType !== 'all') {
      data = data.filter((customer) => customer.vegType === filterPlanType);
    }

    return data;
  }, [customers, searchTerm, filterPlanType]);

  const fetchCustomers = async (requestedPage = 1, append = false, currentSearch = '') => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const res = await http.get('/admin/customers/enhanced', {
        params: {
          page: requestedPage,
          limit,
          status_filter: 'active,upcoming',
          search: currentSearch || '',
        },
      });

      const responseData = res?.data?.data || res?.data || {};
      const list = responseData.customers || responseData.results || responseData.rows || [];
      const total = responseData.totalCount || responseData.total || responseData.count || list.length;

      // Support nested pagination object
      const paginationTotal = responseData.pagination?.total_count || total;

      setCustomers((prev) => (append ? [...prev, ...list] : list));
      setTotalCount(paginationTotal);
      setPage(requestedPage);
      setHasMore(requestedPage * limit < paginationTotal);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError?.(err?.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers(1, false, searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadMoreCustomers = () => {
    if (!hasMore || loadingMore) return;
    fetchCustomers(page + 1, true, searchTerm);
  };

  const openPreferencesModal = (customer) => {
    setSelectedCustomer(customer);
    setShowPreferencesModal(true);
  };

  const closePreferencesModal = () => {
    setShowPreferencesModal(false);
    setSelectedCustomer(null);
  };

  const loadTodayMenu = async (date) => {
    const fetchDate = date || menuDate;
    try {
      const res = await http.get('/admin/menu', { params: { date: fetchDate } });
      const menu = res?.data?.menu || null;

      if (!menu) {
        setMenuData({
          breakfast: { name: '', protein_grams: '', calories: '' },
          lunch: { name: '', protein_grams: '', calories: '' },
          dinner: { name: '', protein_grams: '', calories: '' },
          chicken_meal: { name: '', protein_grams: '', calories: '' },
        });
        return;
      }

      setMenuData({
        breakfast:    { name: menu.breakfast?.name || '',   protein_grams: menu.breakfast?.protein || '',   calories: menu.breakfast?.calories || '' },
        lunch:        { name: menu.lunch?.name || '',       protein_grams: menu.lunch?.protein || '',       calories: menu.lunch?.calories || '' },
        dinner:       { name: menu.dinner?.name || '',      protein_grams: menu.dinner?.protein || '',      calories: menu.dinner?.calories || '' },
        chicken_meal: { name: menu.nonveg_item?.name || '', protein_grams: menu.nonveg_item?.protein || '', calories: menu.nonveg_item?.calories || '' },
      });
    } catch (err) {
      console.error('Error loading menu:', err);
    }
  };

  const getPlanBadge = (customer) => {
    const vegLabel = VEG_TYPE_LABELS[customer.vegType] || customer.vegType || '—';
    const mealLabel = MEAL_COUNT_LABELS[customer.mealCount] || customer.mealCount || '—';
    return `${mealLabel} · ${vegLabel}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-black to-gray-900 rounded-xl p-6 border-2 border-orange-500">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">🍽️ Customer Meal Preferences</h2>
            <p className="text-gray-300 text-sm">View and manage customer meal preferences for customized delivery</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-orange-500">{filteredCustomers.length}</div>
            <div className="text-sm text-gray-300">Total Customers</div>
          </div>
        </div>
      </div>

      {/* Today's Menu Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            setShowMenuModal(true);
            loadTodayMenu();
          }}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-bold shadow-lg transition-all duration-200 flex items-center gap-2"
        >
          <span>📅</span>
          Today's Menu
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-md">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Search Customer</label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
            />
          </div>
          <div className="w-full sm:w-48">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Filter by Plan</label>
            <select
              value={filterPlanType}
              onChange={(e) => setFilterPlanType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm font-medium"
            >
              <option value="all">All Plans</option>
              <option value="5_days_nonveg">5 Days Non-Veg</option>
              <option value="2NV_3V">Mixed (2NV + 3V)</option>
              <option value="5_days_veg">5 Days Veg</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      {loading ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-200 shadow-md">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-900 font-semibold text-lg">Loading customers...</p>
        </div>
      ) : (
        <div className="bg-white shadow-xl overflow-hidden rounded-xl border-2 border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-900 to-black sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-bold text-orange-500 uppercase tracking-wider sticky left-0 z-20 bg-gray-900">
                    Customer
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-orange-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-bold text-orange-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-12 text-center">
                      <div className="text-gray-400 text-lg">
                        <span className="text-4xl mb-2 block">🔍</span>
                        No customers found
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer) => (
                    <tr key={customer.customer_id || customer._id} className="hover:bg-orange-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap sticky left-0 z-10 bg-white">
                        <div>
                          <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
                            {customer.first_name} {customer.last_name}
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              customer.status === 'upcoming'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {customer.status === 'upcoming' ? 'Upcoming' : 'Active'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600">{customer.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900 bg-gray-100 px-3 py-1 rounded-full inline-block">
                          {customer.mealCount && customer.vegType
                            ? getPlanBadge(customer)
                            : customer.meal_plan_type || customer.plan || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => openPreferencesModal(customer)}
                          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-md"
                        >
                          <span className="hidden sm:inline">🍽️ View/Edit Preferences</span>
                          <span className="inline sm:hidden">Edit</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-between items-center mb-3">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold text-gray-900">{customers.length}</span> of{' '}
                <span className="font-semibold text-gray-900">{totalCount}</span> customers
              </div>
            </div>

            {hasMore && (
              <div className="text-center">
                <button
                  onClick={loadMoreCustomers}
                  disabled={loadingMore}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 font-semibold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading more...
                    </span>
                  ) : (
                    `Load More (${customers.length} of ${totalCount})`
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      <MealPreferencesModal
        isOpen={showPreferencesModal}
        onClose={closePreferencesModal}
        customerId={selectedCustomer?.customer_id || selectedCustomer?._id}
        customerName={`${selectedCustomer?.first_name || ''} ${selectedCustomer?.last_name || ''}`.trim()}
        mealCount={selectedCustomer?.mealCount}
        vegType={selectedCustomer?.vegType}
        isAdmin={true}
        setMessage={setMessage}
        setError={setError}
      />

      {/* Today's Menu Modal */}
      {showMenuModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowMenuModal(false)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-black to-gray-900 text-white p-6 rounded-t-xl border-b-4 border-orange-500">
              <h3 className="text-3xl font-bold flex items-center gap-3">
                📅 Today's Menu
              </h3>
              <p className="text-sm mt-2 text-gray-300">View the menu for the selected date</p>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select Date</label>
                <input
                  type="date"
                  value={menuDate}
                  onChange={(e) => {
                    setMenuDate(e.target.value);
                    loadTodayMenu(e.target.value);
                  }}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { label: 'Breakfast', key: 'breakfast', color: 'orange' },
                  { label: 'Lunch', key: 'lunch', color: 'blue' },
                  { label: 'Dinner', key: 'dinner', color: 'purple' },
                  { label: 'Non-Veg / Chicken Meal', key: 'chicken_meal', color: 'red' },
                ].map(({ label, key, color }) => (
                  <div key={key} className={`border-2 border-${color}-300 rounded-lg p-4 bg-gradient-to-br from-${color}-50 to-white`}>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">{label}</h4>
                    <div className="space-y-3">
                      <input type="text" readOnly value={menuData[key].name} placeholder="—" className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700" />
                      <div className="grid grid-cols-2 gap-2">
                        <input type="text" readOnly value={menuData[key].protein_grams ? `${menuData[key].protein_grams}g protein` : '—'} className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700" />
                        <input type="text" readOnly value={menuData[key].calories ? `${menuData[key].calories} cal` : '—'} className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-4 border-t-2 border-orange-500">
                <button
                  onClick={() => setShowMenuModal(false)}
                  className="flex-1 border-2 border-gray-300 hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-lg font-bold transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreferencesTab;
